import { ArrowRight, CheckCircle2, Chrome, LockKeyhole, Mail, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Seo } from '../lib/seo';
import { clearStoredOAuthNextPath, getStoredOAuthNextPath } from '../lib/supabase';
import { Logo } from '../components/Logo';
import { FREE_ACCOUNT_CREDITS } from '../../shared/billing';

function safeNextPath(value: string | null) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/tools';
  return value;
}

export function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'signup'>(() => params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { signIn, signUp, signInWithGoogle, updatePassword, user, enabled } = useAuth();
  const navigate = useNavigate();
  const destination = useMemo(() => safeNextPath(params.get('next') ?? getStoredOAuthNextPath()), [params]);
  const recoveryMode = params.get('mode') === 'recovery' || new URLSearchParams(window.location.hash.slice(1)).get('type') === 'recovery';

  useEffect(() => {
    setMode(params.get('mode') === 'signup' ? 'signup' : 'signin');
  }, [params]);

  useEffect(() => {
    if (user && !recoveryMode) { clearStoredOAuthNextPath(); navigate(destination, { replace: true }); }
  }, [user, navigate, destination, recoveryMode]);

  async function handleGoogleSignIn() {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await signInWithGoogle(destination);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      if (recoveryMode) {
        await updatePassword(password);
        navigate(destination, { replace: true });
      } else if (mode === 'signin') {
        await signIn(email, password);
        navigate(destination, { replace: true });
      } else {
        const result = await signUp(email, password);
        if (result === 'confirm-email') {
          setMessage('Account created. Check your email and confirm the address, then sign in.');
          setMode('signin');
          setPassword('');
          return;
        }
        navigate(destination, { replace: true });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="auth-page">
      <Seo title="Sign In or Create an Account" description="Sign in to Nexhance AI to manage credits, subscriptions and AI image-processing history." path="/auth" noindex />
      <div className="auth-visual">
        <div className="auth-visual-inner">
          <Logo />
          <p className="eyebrow">A focused image workspace</p>
          <h1>Keep every enhancement in one clean, professional workflow.</h1>
          <p>Sign in to receive and use account credits, revisit recent jobs and manage billing from one professional account area.</p>
          <div className="auth-benefits">
            <span><CheckCircle2 /> All AI workflows plus free browser utilities</span>
            <span><CheckCircle2 /> Credit balance and job history</span>
            <span><CheckCircle2 /> Secure hosted checkout</span>
          </div>
          <div className="auth-art"><Sparkles size={28} /><span /><span /><span /></div>
        </div>
      </div>
      <div className="auth-panel">
        <div className="auth-box">
          <Logo className="auth-mobile-logo" />
          <p className="eyebrow">{recoveryMode ? 'Password recovery' : mode === 'signin' ? 'Welcome back' : 'Create your account'}</p>
          <h2>{recoveryMode ? 'Choose a new password' : mode === 'signin' ? 'Sign in to Nexhance AI' : `Start with ${FREE_ACCOUNT_CREDITS} lifetime free credits`}</h2>
          <p>{recoveryMode ? 'Enter a new password for this account. The recovery link is validated by Supabase before this page can update it.' : mode === 'signin' ? 'Continue to your account, credits and image-processing tools.' : 'Create an account and sign in to receive the one-time free-credit grant. Signed-out visitors do not have a credit balance.'}</p>

          {!enabled && (
            <div className="auth-config-note">
              <LockKeyhole size={18} />
              <div><strong>Supabase Authentication is not configured.</strong><span>Add the Supabase project URL and publishable key to enable real accounts.</span></div>
            </div>
          )}

          {!recoveryMode && <>
            <button type="button" className="button button-ghost google-button" onClick={() => void handleGoogleSignIn()} disabled={!enabled || loading}>
              <Chrome size={18} /> Continue with Google
            </button>
            <div className="or-divider"><span /> or continue with email <span /></div>
          </>}
          <form onSubmit={submit}>
            {!recoveryMode && <label>
              <span>Email address</span>
              <div className="input-with-icon"><Mail size={18} /><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required placeholder="you@example.com" /></div>
            </label>}
            <label>
              <span>Password</span>
              <div className="input-with-icon"><LockKeyhole size={18} /><input type="password" minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} required placeholder="At least 8 characters" /></div>
            </label>
            {error && <div className="inline-error">{error}</div>}
            {message && <div className="inline-success">{message}</div>}
            <button className="button button-primary button-large auth-submit" disabled={!enabled || loading}>
              {loading ? 'Please wait…' : recoveryMode ? 'Update password' : mode === 'signin' ? 'Sign in' : 'Create account'} <ArrowRight size={18} />
            </button>
          </form>
          {!recoveryMode && <p className="auth-switch">
            {mode === 'signin' ? 'New to Nexhance AI?' : 'Already have an account?'}{' '}
            <button onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); setMessage(''); }}>
              {mode === 'signin' ? 'Create an account' : 'Sign in'}
            </button>
          </p>}
        </div>
      </div>
    </section>
  );
}
